import { ConfigData } from "./config";
import { hashFraction } from "./hash";
import { Item, Point } from "./model";

/**
 * Geometry and numbering of the single quadrant view.
 *
 * The zoomed quadrant is always drawn on a quarter circle, whatever the number
 * of segments the radar has - 90 degrees for the four Tech quadrants, and the
 * same 90 degrees for the six AI segments even though they occupy 60 on the full
 * chart. Nothing is lost by stretching them: the angle of a blip carries no
 * meaning, it comes out of `hashFraction`, and only the distance to the centre
 * says something. Reusing the same hash seeds as `BlipPoints` keeps each blip at
 * the same relative place it holds on the full chart.
 *
 * The centre of the radar sits at the bottom right corner of the box, so the
 * rings widen up and to the left and the ring labels read from the centre
 * outwards along the bottom axis - Adopt on the right, Hold on the left.
 */

export type NumberedEntry = {
  item: Item;
  /** 1-based, shared by the blip and its row in the list. */
  number: number;
  /** Index in `config.rings`, 0 being the innermost ring. */
  ringIndex: number;
};

/**
 * Entries of one quadrant, numbered by ring then alphabetically.
 *
 * The numbering is the whole point of the view: blip 1 is the first entry of the
 * innermost ring, so the eye goes from a number on the chart to a paragraph in
 * the list without searching. Items whose ring is unknown to the radar are
 * dropped rather than numbered into a ring they do not belong to.
 */
export const numberedEntries = (
  items: Item[],
  config: ConfigData,
  quadrant: string
): NumberedEntry[] => {
  const inQuadrant = items.filter((item) => item.quadrant === quadrant);

  return config.rings
    .flatMap((ring) =>
      inQuadrant
        .filter((item) => item.ring === ring)
        .sort((a, b) => a.title.localeCompare(b.title))
        .map((item) => ({
          item,
          ringIndex: config.rings.indexOf(ring),
          number: 0,
        }))
    )
    .map((entry, index) => ({ ...entry, number: index + 1 }));
};

/** Entries of a single ring, in numbering order. */
export const entriesOfRing = (entries: NumberedEntry[], ringIndex: number) =>
  entries.filter((entry) => entry.ringIndex === ringIndex);

export type ZoomGeometry = {
  /** Side of the square box, in user units. The centre is at (size, size). */
  size: number;
  /** Outer radius of each ring, innermost first, scaled to the box. */
  ringRadii: number[];
  blipRadius: number;
};

/**
 * Ring radii scaled from `config.chartConfig`, so the bands keep the proportions
 * of the full chart: on the Tech radar, adopt reaches half of the outer radius.
 */
export const zoomGeometry = (
  config: ConfigData,
  size: number,
  blipRadius = 13
): ZoomGeometry => {
  const attributes = config.chartConfig.ringsAttributes;
  const outer = attributes[attributes.length - 1].radius;

  return {
    size,
    ringRadii: attributes.map((ring) => (ring.radius / outer) * size),
    blipRadius,
  };
};

/** Keeps blips off the arcs and off the two straight edges. */
const ANGLE_INSET = 0.08;
const RADIUS_PADDING = 1.6;
const MAX_PLACEMENT_ATTEMPTS = 60;

const fractionFor = (name: string, kind: string, attempt: number) =>
  hashFraction(`${name}:${kind}:${attempt}`);

const pointAt = (
  geometry: ZoomGeometry,
  ringIndex: number,
  angleFraction: number,
  radiusFraction: number
): Point => {
  const inner = ringIndex === 0 ? 0 : geometry.ringRadii[ringIndex - 1];
  const outer = geometry.ringRadii[ringIndex];
  const padding = geometry.blipRadius * RADIUS_PADDING;
  const min = inner + padding;
  const max = Math.max(min, outer - padding);

  const radius = min + radiusFraction * (max - min);
  const angle =
    ((ANGLE_INSET + angleFraction * (1 - 2 * ANGLE_INSET)) * Math.PI) / 2;

  return {
    x: geometry.size - Math.cos(angle) * radius,
    y: geometry.size - Math.sin(angle) * radius,
  };
};

const distance = (a: Point, b: Point) =>
  Math.sqrt((a.x - b.x) ** 2 + (a.y - b.y) ** 2);

/**
 * Places every blip of the quadrant, deterministically.
 *
 * Attempt 0 uses the same seeds as the full chart; later attempts are indexed on
 * the attempt number so a blip that collides moves somewhere else rather than
 * being regenerated at the same point sixty times. Numbers have to stay
 * readable, so two blips may not overlap.
 */
export const placeEntries = (
  entries: NumberedEntry[],
  geometry: ZoomGeometry
): (NumberedEntry & { point: Point })[] =>
  entries.reduce<(NumberedEntry & { point: Point })[]>((placed, entry) => {
    let point = pointAt(geometry, entry.ringIndex, 0.5, 0.5);

    for (let attempt = 0; attempt < MAX_PLACEMENT_ATTEMPTS; attempt++) {
      point = pointAt(
        geometry,
        entry.ringIndex,
        fractionFor(entry.item.name, "angle", attempt),
        fractionFor(entry.item.name, "radius", attempt)
      );

      const overlaps = placed.some(
        (other) => distance(point, other.point) < geometry.blipRadius * 2.2
      );
      if (!overlaps) {
        break;
      }
    }

    placed.push({ ...entry, point });
    return placed;
  }, []);

/** Mid-band radius of a ring, where its label sits on the bottom axis. */
export const ringLabelRadius = (geometry: ZoomGeometry, ringIndex: number) => {
  const inner = ringIndex === 0 ? 0 : geometry.ringRadii[ringIndex - 1];
  return inner + (geometry.ringRadii[ringIndex] - inner) / 2;
};

/**
 * First words of an item's body, as plain text.
 *
 * Bodies are HTML produced by the generator; the list needs a teaser, not a
 * document. Cuts on a word boundary and appends nothing - the "Read more" link
 * that follows says what the ellipsis would.
 */
export const excerpt = (body: string, maxChars = 260): string => {
  const text = body
    .replace(/<[^>]*>/g, " ")
    .replace(/&#39;/g, "'")
    .replace(/&quot;/g, '"')
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    // A tag replaced by a space leaves "broker ." whenever the punctuation sat
    // outside it, which is how the generator writes "<strong>broker</strong>.".
    .replace(/\s+([.,;:!?)\]])/g, "$1")
    .trim();

  if (text.length <= maxChars) {
    return text;
  }

  const cut = text.slice(0, maxChars);
  const lastSpace = cut.lastIndexOf(" ");
  return `${(lastSpace > 0 ? cut.slice(0, lastSpace) : cut).replace(
    /[,;:.\s]+$/,
    ""
  )}…`;
};
